import React  from 'react';
import {buttonStyles} from "../../../config/Theme";
import { Theme, Tooltip, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import ResponsiveButtonWithTooltip, {ResponsiveButtonWithTooltipProps} from "./ResponsiveButtonWithTooltip";
import {useDispatch} from "react-redux";
import {sendUserToPage} from "../../../actions/common/UserViewSettingsActions";

/**
 * Represents the properties required to create a link-styled button component.
 * This component is designed to provide both navigation and visual emphasis where needed.
 *
 * This interface extends the `ResponsiveButtonWithTooltipProps`, inheriting all its responsive and tooltip-related functionalities.
 * It includes properties to configure the visual state of the button, the navigation path, and additional behavior for different use cases.
 *
 * @interface
 *
 * @extends ResponsiveButtonWithTooltipProps
 *
 * @param {boolean} [large] Optional. Specifies whether the button should be displayed in a larger, more prominent style.
 *                          If `true`, the button adopts larger dimensions and typography for emphasis.
 *
 * @param {string} path Defines the navigation path or URL the button should lead to when clicked.
 *                      This is a required property and must be supplied to enable navigation behavior.
 *
 * @param {string} tooltip Defines the tooltip text displayed when the user hovers over the button.
 *                         Helps provide context or additional information for the button's purpose.
 *
 * @param {string} text Specifies the text label displayed on the button. This should be clear and concise,
 *                      summarizing the button's action or intent.
 *
 * @param {React.ReactNode} icon Represents the icon displayed alongside the button text.
 *                               This adds visual context to the button's purpose or action.
 *
 * @param {boolean} [important] Optional. When `true`, this flag highlights the button as important,
 *                              potentially altering its visual style or priority in the UI.
 */
export interface LinkButtonProps extends ResponsiveButtonWithTooltipProps {
    large?: boolean
    path: string
    tooltip: string
    text: string
    icon: React.ReactNode
    important?: boolean
}

/**
 * Generates and provides reusable style definitions to be applied within the component.
 *
 * The `useStyle` variable centralizes visual style configurations for UI consistency and responsiveness across components. It facilitates compliance with design guidelines and seamless theme integration.
 *
 * This style object includes custom button styles, responsive section layouts, and hover states. It is built using Material-UI's `makeStyles` utility, ensuring compatibility with the defined `theme`.
 *
 * **Key Details:**
 * - Combines specific button styles merged with a shared style base from `buttonStyles(theme)`.
 * - Provides conditional rendering and dynamic layout styles for desktop screen sizes using Material-UI's breakpoint utilities.
 * - Employs theme spacing and primary palette values for consistency with the overarching application theme.
 * - Ideal for components that require responsive visual organization and consistent button theming.
 *
 * **Tags:**
 * @returns An object containing classes as string keys that map to specific styles, useful for applying to JSX elements via the `className` property.
 * @throws Will throw an error if the `theme` object is improperly configured or missing required properties.
 */
const useStyle = makeStyles(( theme: Theme ) => ({
        ...buttonStyles(theme),
        tableButtonStyle: {
            color: "rgba(0, 0, 0, 0.54)",
            padding: theme.spacing(),
            '&:hover': {
                color: theme.palette.primary.main
            }
        },
        sectionDesktop: {
            display: 'none',
            [theme.breakpoints.up('md')]: {
                paddingRight: "10px",
                paddingLeft: "10px",
                display: "flex"
            },
        },
    })
);

/**
 * Renders a button component styled as a link, allowing users to navigate
 * to a specified path or perform custom actions when clicked.
 *
 * This component caters to varying button sizes and integrates a tooltip for
 * enhanced usability. It can either use a default navigation behavior or a custom
 * click handler supplied via props. The visual style is responsive, adapting
 * based on size preferences.
 *
 * @param props - An object containing configuration for the button's
 * functionality and appearance.
 * @param props.large - If `true`, renders a larger variant of the button. Defaults to `false`.
 * @param props.tooltip - A string specifying the tooltip text shown on hover.
 * @param props.text - The display text for the button.
 * @param props.icon - An optional icon displayed alongside the text.
 * @param props.path - A string representing the path the user is directed to
 * if no custom click handler is provided.
 * @param props.onClick - An optional custom click handler. If not provided,
 * default navigation to the `path` value is executed.
 *
 * @return A React element representing the styled, interactive link button.
 *
 * @throws Throws an error at runtime if required properties such as `path` or
 * `text` are missing, as these are essential for functionality and user experience.
 */
function LinkButton( props : LinkButtonProps) {
    const classes = useStyle();
    const dispatch = useDispatch();

    if( props.large ) {
        return (

                <ResponsiveButtonWithTooltip
                    { ...props }
                    tooltip={ props.tooltip }
                    text={ props.text }
                    icon={ props.icon }
                    onClick={ typeof props.onClick === 'undefined'
                        ? () => { dispatch( sendUserToPage( props.path, '' ) ) }
                        : props.onClick
                }
                />
        );
    } else {
        return (
            <ResponsiveButtonWithTooltip
                { ...props }
                variant={ 'xs' }
                tooltip={ props.tooltip }
                text={ props.text }
                icon={ props.icon }
                onClick={ typeof props.onClick === 'undefined'
                    ? () => { dispatch( sendUserToPage( props.path , '' ) ) }
                    : props.onClick
                }
            />
        );
    }

}

export default LinkButton;